/**
 * ContactRequestForm
 *
 * Section 3 of the Contact page: the request-content textarea, the email
 * textarea, and the Submit button. The Submit button stays enabled at all
 * times — validation runs on tap (Flutter parity per the task plan's
 * "Behavior the web must replicate exactly" → item 1). The full-page
 * loading overlay rendered by `ContactPage` covers this section while a
 * submission is in flight, preventing double-tap submissions.
 *
 * Co-located with `ContactPage` to keep the page under the ~200-line
 * Should-Fix threshold from `presentation.md`.
 *
 * Dual-use note: also reused verbatim by `SupportPage` (the Support list
 * page renders the same content + email + Submit form below the category
 * cards). The only divergence is the leading label text — pass `labelText`
 * to override the default `l10n.contact.requestSectionLabel`. Both pages
 * funnel through the same `useContact` hook + `submitSupportRequestUseCase`,
 * so the form's submit surface is identical.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InputText } from '@/presentation/components/common/InputText';
import { ExpauseButton, ButtonVariant } from '@/presentation/components/common/controls/ExpauseButton';
import { InputLengthFormatters } from '@/presentation/constants/formatters/inputLengthFormatters';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ContactRequestFormProps {
    content: string;
    email: string;
    setContent: (value: string) => void;
    setEmail: (value: string) => void;
    onSubmit: () => void;
    /**
     * Optional override for the section's leading label. Defaults to
     * `l10n.contact.requestSectionLabel`. The Support list page passes
     * `l10n.support.requestSectionLabel` here to reuse this form
     * verbatim with its own copy.
     */
    labelText?: string;
    /**
     * Prefix for the `data-qa-*` ids on this form's fields and submit button,
     * keeping ids page-unique across the two reuse sites (Contact vs Support).
     * Defaults to `'contact'` → `contact-content` / `contact-email` /
     * `contact-submit`.
     */
    qaIdPrefix?: string;
}

export const ContactRequestForm = ({
    content,
    email,
    setContent,
    setEmail,
    onSubmit,
    labelText,
    qaIdPrefix = 'contact',
}: ContactRequestFormProps) => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();

    return (
        <section
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: responsive.spacing.base,
            }}
        >
            <p
                style={{
                    margin: 0,
                    marginBottom: responsive.spacing.base,
                    color: theme.text.primary,
                    fontSize: responsive.font.base,
                    fontWeight: responsive.fontWeight.semibold,
                    textAlign: 'center',
                }}
            >
                {labelText ?? l10n.contact.requestSectionLabel}
            </p>

            <div
                style={{
                    backgroundColor: theme.secondary,
                    borderRadius: responsive.borderRadius.base,
                    overflow: 'hidden',
                    marginBottom: responsive.spacing.base,
                }}
            >
                <InputText
                    qa={{ id: `${qaIdPrefix}-content` }}
                    value={content}
                    onChange={setContent}
                    maxLength={InputLengthFormatters.contactRequestMaxLength}
                    placeholder={l10n.contact.contentPlaceholder}
                    rows={4}
                    multiline
                    backgroundColor="transparent"
                    height={responsive.form.textareaHeight}
                    padding={responsive.spacing.base}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                />
            </div>

            <div
                style={{
                    backgroundColor: theme.secondary,
                    borderRadius: responsive.borderRadius.base,
                    overflow: 'hidden',                    
                }}
            >
                <InputText
                    qa={{ id: `${qaIdPrefix}-email` }}
                    value={email}
                    onChange={setEmail}
                    maxLength={InputLengthFormatters.contactRequestMaxLength}
                    placeholder={l10n.contact.emailPlaceholder}
                    multiline={false}
                    backgroundColor="transparent"
                    padding={responsive.spacing.base}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                />
            </div>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: responsive.spacing.base,
                }}
            >
                <ExpauseButton
                    {...qaAttr({ id: `${qaIdPrefix}-submit` })}
                    variant={ButtonVariant.primary}
                    onClick={onSubmit}
                >
                    {l10n.contact.submitButton}
                </ExpauseButton>
            </div>
        </section>
    );
};
